import React, { useState, useEffect } from 'react';
import { User, Calendar, X } from 'lucide-react';
import userService from '../../../../services/userService';
import { useToast } from '../../../../context/ToastContext'; 


export default function UpdateRiskModal({
  show,
  updateData,
  setUpdateData,
  probabiliteOptions,
  onClose,
  onSubmit,
  authTokens,
}) {
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);
  const { showSuccess, showError, showWarning } = useToast();

  // Initialize with existing data if editing
  useEffect(() => {
    if (updateData.assignedUser) {
      setSelectedUser(updateData.assignedUser);
      setUserSearchQuery(`${updateData.assignedUser.first_name} ${updateData.assignedUser.last_name} (${updateData.assignedUser.email})`);
    }
    if (updateData.selectedRole) {
      setSelectedRole(updateData.selectedRole);
    }
  }, [updateData.assignedUser, updateData.selectedRole]);

  if (!show) return null;

  // Use actual backend service
  const searchUsers = async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      setShowUserDropdown(false);
      return;
    }

    try {
      const result = await userService.filterUsersBySearch(query, authTokens.access);
      if (result.success) {
        setSearchResults(result.data);
        setShowUserDropdown(true);
      } else {
        console.error('Error searching users:', result.error);
        setSearchResults([]);
        setShowUserDropdown(false);
      }
    } catch (error) {
      console.error('Error searching users:', error);
      setSearchResults([]);
      setShowUserDropdown(false);
    }
  };

  const handleUserSearchInput = (query) => {
    setUserSearchQuery(query);
    setSelectedUser(null);
    setSelectedRole(null);
    searchUsers(query);
  };

  const selectUser = (user) => {
    const userDisplay = `${user.first_name} ${user.last_name} (${user.email})`;
    setUserSearchQuery(userDisplay);
    setSelectedUser(user);
    setSelectedRole(null);
    setShowUserDropdown(false);
    setSearchResults([]);
  };

  const handleRoleSelect = (role) => {
    setSelectedRole(role);
  };

  const clearUserSelection = () => {
    setUserSearchQuery('');
    setSelectedUser(null);
    setSelectedRole(null);
  };

  const handleSubmitWithValidation = () => {
    // Check if user is selected and role is required
    // if (selectedUser) {
    //   showError("Veuillez sélectionner l'utilisateur assigné. pour");
    //   return;
    // }

    // Prepare data with new fields - use local state values
    const updatedData = {
      ...updateData,
      assignedUser: selectedUser,
      selectedRole: selectedRole,
      assignedUserId: selectedUser?.id || null,
      userRoleAssignedId: selectedRole?.id || null
    };

    onSubmit(updatedData);
  };

  // Safe check for roles array
  const userRoles = selectedUser?.roles || [];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-semibold mb-4">Mettre à jour le risque</h3>

        {/* First Row - Probability and Consequence */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          {/* Probability Field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Probabilité
            </label>
            <select
              value={updateData.probability}
              onChange={(e) =>
                setUpdateData({ ...updateData, probability: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              <option value="">Sélectionner...</option>
              {probabiliteOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Consequence Field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Conséquence
            </label>
            <textarea
              value={updateData.consequence}
              onChange={(e) =>
                setUpdateData({ ...updateData, consequence: e.target.value })
              }
              placeholder="Décrire la conséquence..."
              rows="3"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          </div>
        </div>

        {/* Second Row - Due Date and User Assignment */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          {/* Due Date Field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Calendar className="w-4 h-4 inline mr-1" />
              Date d'échéance
            </label>
            <input
              type="date"
              value={updateData.dueDate || ''}
              onChange={(e) =>
                setUpdateData({ ...updateData, dueDate: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          </div>

          {/* User Assignment Field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <User className="w-4 h-4 inline mr-1" />
              Utilisateur assigné
            </label>
            <div className="relative">
              <input
                type="text"
                value={userSearchQuery}
                onChange={(e) => handleUserSearchInput(e.target.value)}
                placeholder="Rechercher un utilisateur (min 2 caractères)..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
              />

              {/* User Search Dropdown */}
              {showUserDropdown && searchResults.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-y-auto">
                  {searchResults.map((user) => (
                    <button
                      key={user.id}
                      type="button"
                      onClick={() => selectUser(user)}
                      className="w-full px-3 py-2 text-left hover:bg-gray-50 border-b last:border-b-0"
                    >
                      <div className="font-medium text-sm">
                        {user.first_name} {user.last_name}
                      </div>
                      <div className="text-gray-500 text-xs">
                        {user.email}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Third Row - Action Description (Full Width) */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Action préventive/corrective
          </label>
          <textarea
            value={updateData.action || ''}
            onChange={(e) =>
              setUpdateData({ ...updateData, action: e.target.value })
            }
            placeholder="Décrire l'action à entreprendre..."
            rows="3"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
          />
        </div>

        {/* Role Selection - Shows after user is selected */}
        {selectedUser && (
          <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded-md">
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-700">
                Rôle/position de l'utilisateur sélectionné: {selectedUser.role_function}
              </label>

            </div>
          </div>
        )}

        {/* Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 justify-end mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded hover:bg-gray-200 transition-colors"
          >
            Annuler
          </button>
          <button
            onClick={handleSubmitWithValidation}
            disabled={!updateData.probability}
            className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors disabled:bg-green-300 disabled:cursor-not-allowed"
          >
            Mettre à jour
          </button>
        </div>
      </div>
    </div>
  );
}